import {
  createDatabaseBlockDataSource,
  isDatabaseStorageModelReady,
} from '@blocksuite/affine/blocks/database';

export interface DatabaseSelectionCandidate {
  id: string;
  model: unknown;
  title?: string;
  titleLoading?: boolean;
}

function readModelTitle(model: unknown): string {
  const props = (model as { props?: { title?: { toString(): string } } })
    ?.props;
  return props?.title?.toString().trim() || 'Untitled';
}

function scoreDatabaseModel(model: unknown): {
  readonly title: string;
  readonly score: number;
} {
  try {
    if (!isDatabaseStorageModelReady(model as never)) {
      return { title: readModelTitle(model), score: -1 };
    }

    const dataSource = createDatabaseBlockDataSource(model as never);
    const storageModel = dataSource.storageModel as {
      props?: {
        title?: { toString(): string };
        ['columns$']?: { value?: unknown[] };
      };
    };
    const title =
      storageModel.props?.title?.toString().trim() || readModelTitle(model);
    const columnCount = storageModel.props?.['columns$']?.value?.length ?? 0;
    const rowCount = (dataSource as { ['rowCount$']: { value: number } })[
      'rowCount$'
    ].value;
    return { title: title || 'Untitled', score: rowCount * 1000 + columnCount };
  } catch {
    return { title: readModelTitle(model), score: -1 };
  }
}

function normalizeTitle(value: string): string {
  return value.trim().toLocaleLowerCase();
}

export function orderCanonicalDatabaseCandidates<
  T extends DatabaseSelectionCandidate,
>(candidates: readonly T[]): T[] {
  const ranked = candidates.map((candidate, index) => {
    const score = scoreDatabaseModel(candidate.model);
    const title =
      candidate.title && !candidate.titleLoading
        ? candidate.title
        : score.title;
    return {
      candidate,
      index,
      key: normalizeTitle(title),
      score: score.score,
    };
  });

  ranked.sort((a, b) => {
    if (a.key !== b.key) {
      return a.index - b.index;
    }
    return b.score - a.score || a.index - b.index;
  });

  return ranked.map(entry => entry.candidate);
}
